/**
 * Locked-tree refinements for `macroTreeGraphSchema`.
 *
 * Every rule here runs only on the LOCKED slot (`macroTreeGraphSchema`); the
 * draft schema never calls into this file. Rules are applied in a fixed order
 * and stop at the first issue so error output stays deterministic and tests
 * can assert on paths without depending on later rules.
 *
 *   1. identity   — unique node/edge/snapshot/fork ids, root and edges resolve
 *   2. topology   — acyclic, every node reachable from the root
 *   3. per-trigger:
 *        `launch`  — node kinds, branch siblings, Wait nodes, screenshot-wait
 *        `watcher` — exactly one node: the root, with a reference and no actions
 */
import { z } from 'zod';
import type { MacroGroup, MacroTreeEdge, MacroTreeGraphInput } from './macroTree';
import { refineScreenshotWaitLock } from './macroTreeScreenshotWaitRefine';
import { isDedicatedWait, validateWaitSiblingCoherence } from './macroTreeWaitSiblingCoherence';

/** Check deadline bounds for a `watcher` tree's single node (`waitTimeoutMs`). */
export const MACRO_WATCHER_DEADLINE_MIN_MS = 1_000;
export const MACRO_WATCHER_DEADLINE_MAX_MS = 30_000;

type Issue = { message: string; path: (string | number)[] };

function issue(ctx: z.RefinementCtx, found: Issue): true {
  ctx.addIssue({ code: z.ZodIssueCode.custom, message: found.message, path: found.path });
  return true;
}

/** Returns true iff the directed graph defined by (nodeIds, edges) is acyclic. Kahn's algorithm — O(N + E). */
function isAcyclic(nodeIds: ReadonlyArray<string>, edges: ReadonlyArray<MacroTreeEdge>): boolean {
  const inDegree = new Map<string, number>();
  const outgoing = new Map<string, string[]>();
  for (const id of nodeIds) inDegree.set(id, 0);
  for (const edge of edges) {
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1);
    const targets = outgoing.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoing.set(edge.source, targets);
  }
  const queue: string[] = [];
  for (const [id, deg] of inDegree.entries()) {
    if (deg === 0) queue.push(id);
  }
  let processed = 0;
  for (let cursor = 0; cursor < queue.length; cursor++) {
    const id = queue[cursor];
    processed++;
    for (const target of outgoing.get(id) ?? []) {
      const next = (inDegree.get(target) ?? 0) - 1;
      inDegree.set(target, next);
      if (next === 0) queue.push(target);
    }
  }
  return processed === nodeIds.length;
}

/** Returns true iff every node is reachable from `rootNodeId` via source → target edges. BFS — O(N + E). */
function allNodesReachableFromRoot(
  rootNodeId: string,
  nodeIds: ReadonlyArray<string>,
  edges: ReadonlyArray<MacroTreeEdge>,
): boolean {
  const reachable = new Set<string>([rootNodeId]);
  const outgoing = new Map<string, string[]>();
  for (const edge of edges) {
    const targets = outgoing.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoing.set(edge.source, targets);
  }
  const queue: string[] = [rootNodeId];
  for (let cursor = 0; cursor < queue.length; cursor++) {
    const current = queue[cursor];
    for (const target of outgoing.get(current) ?? []) {
      if (reachable.has(target)) continue;
      reachable.add(target);
      queue.push(target);
    }
  }
  return nodeIds.every(id => reachable.has(id));
}

function childrenByParent(edges: ReadonlyArray<MacroTreeEdge>): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const edge of edges) {
    const list = map.get(edge.source) ?? [];
    list.push(edge.target);
    map.set(edge.source, list);
  }
  return map;
}

function firstDuplicate(ids: ReadonlyArray<string>): string | undefined {
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) return id;
    seen.add(id);
  }
  return undefined;
}

/** Unique ids and resolvable references. Returns true when an issue was added. */
function refineIdentity(graph: MacroTreeGraphInput, ctx: z.RefinementCtx): boolean {
  const nodeIds = graph.nodes.map(n => n.id);
  const duplicateNode = firstDuplicate(nodeIds);
  if (duplicateNode !== undefined) return issue(ctx, { message: `duplicate node id: ${duplicateNode}`, path: ['nodes'] });
  const duplicateEdge = firstDuplicate(graph.edges.map(e => e.id));
  if (duplicateEdge !== undefined) return issue(ctx, { message: `duplicate edge id: ${duplicateEdge}`, path: ['edges'] });
  const known = new Set(nodeIds);
  if (!known.has(graph.rootNodeId)) {
    return issue(ctx, { message: 'rootNodeId must reference a node in nodes', path: ['rootNodeId'] });
  }
  for (const edge of graph.edges) {
    if (!known.has(edge.source) || !known.has(edge.target)) {
      return issue(ctx, { message: `edge ${edge.id} references unknown node`, path: ['edges'] });
    }
  }
  for (const node of graph.nodes) {
    if (!node.expectedState) continue;
    const duplicateSnapshot = firstDuplicate(node.expectedState.snapshots.map(s => s.id));
    if (duplicateSnapshot !== undefined) {
      return issue(ctx, { message: `duplicate snapshot id ${duplicateSnapshot} in node ${node.id}`, path: ['nodes'] });
    }
  }
  // Forks live in metadata as a UI sidecar, but their ids are referenced by
  // other forks' `inputs`/`outputs` for chained fan-out/fan-in. Duplicates
  // would make those references ambiguous and break editor reconstruction.
  const duplicateFork = firstDuplicate((graph.metadata.forks ?? []).map(f => f.id));
  if (duplicateFork !== undefined) {
    return issue(ctx, { message: `duplicate fork id: ${duplicateFork}`, path: ['metadata', 'forks'] });
  }
  return false;
}

function refineTopology(graph: MacroTreeGraphInput, ctx: z.RefinementCtx): boolean {
  const nodeIds = graph.nodes.map(n => n.id);
  if (!isAcyclic(nodeIds, graph.edges)) return issue(ctx, { message: 'graph must be acyclic', path: ['edges'] });
  if (!allNodesReachableFromRoot(graph.rootNodeId, nodeIds, graph.edges)) {
    return issue(ctx, { message: 'every node must be reachable from rootNodeId', path: ['edges'] });
  }
  return false;
}

/**
 * Node-kind invariants for `launch` trees: exactly one `start` (= root), at
 * least one `end`, every leaf is `end` and every `end` is a leaf. Together
 * these let the runtime use `kind === 'end'` as the definitive terminal signal.
 */
function refineNodeKinds(graph: MacroTreeGraphInput, ctx: z.RefinementCtx): boolean {
  const startNodes = graph.nodes.filter(n => n.kind === 'start');
  if (startNodes.length !== 1) {
    return issue(ctx, { message: `expected exactly 1 node with kind === 'start' (got ${startNodes.length})`, path: ['nodes'] });
  }
  if (startNodes[0].id !== graph.rootNodeId) {
    return issue(ctx, { message: "the 'start' node's id must equal rootNodeId", path: ['rootNodeId'] });
  }
  if (!graph.nodes.some(n => n.kind === 'end')) {
    return issue(ctx, { message: "expected at least 1 node with kind === 'end'", path: ['nodes'] });
  }
  const outDegree = new Map<string, number>();
  for (const node of graph.nodes) outDegree.set(node.id, 0);
  for (const edge of graph.edges) outDegree.set(edge.source, (outDegree.get(edge.source) ?? 0) + 1);
  for (const node of graph.nodes) {
    const isLeaf = (outDegree.get(node.id) ?? 0) === 0;
    if (isLeaf && node.kind !== 'end') {
      return issue(ctx, { message: `node ${node.id} has no outgoing edges but kind !== 'end'`, path: ['nodes'] });
    }
    if (node.kind === 'end' && !isLeaf) {
      return issue(ctx, { message: `node ${node.id} is kind: 'end' but has outgoing edges`, path: ['nodes'] });
    }
  }
  return false;
}

/**
 * Multi-child invariant: when a parent has >1 child, every child must carry
 * `expectedState` so the runtime can distinguish branches — except dedicated
 * Wait siblings, which carry references on *their* children. A single child
 * may omit `expectedState` (reference-optional linear advance).
 */
function refineBranchSiblings(graph: MacroTreeGraphInput, ctx: z.RefinementCtx): boolean {
  for (const [parentId, childIds] of childrenByParent(graph.edges).entries()) {
    if (childIds.length <= 1) continue;
    const siblings = childIds
      .map(id => graph.nodes.find(n => n.id === id))
      .filter((n): n is MacroGroup => n !== undefined);
    for (const found of validateWaitSiblingCoherence(parentId, siblings)) {
      return issue(ctx, { message: found.message, path: ['nodes'] });
    }
    for (const child of siblings) {
      if (isDedicatedWait(child)) continue;
      if (!child.expectedState) {
        return issue(ctx, {
          message: `node ${child.id} has no expectedState but is one of ${childIds.length} children of node ${parentId} — all siblings must have screenshots when branching`,
          path: ['nodes'],
        });
      }
    }
  }
  return false;
}

/** Wait-node invariants (ERROR_STATES A1–A9). */
function refineWaitNode(graph: MacroTreeGraphInput, node: MacroGroup, ctx: z.RefinementCtx): boolean {
  const path = ['nodes'];
  if (node.id === graph.rootNodeId) {
    return issue(ctx, { message: `node ${node.id} is kind: 'wait' but is the root — Wait cannot be start/root`, path });
  }
  if (node.expectedState) {
    return issue(ctx, { message: `node ${node.id} is kind: 'wait' but has expectedState — Wait must not carry a screenshot`, path });
  }
  if (node.macros.length > 0) {
    return issue(ctx, { message: `node ${node.id} is kind: 'wait' but has macros — Wait must not carry macros`, path });
  }
  if (node.postMacroDelayMs !== 0) {
    return issue(ctx, { message: `node ${node.id} is kind: 'wait' but postMacroDelayMs is ${node.postMacroDelayMs} — must be 0`, path });
  }
  if (node.waitIntervalMs === undefined || node.waitTimeoutMs === undefined) {
    return issue(ctx, { message: `node ${node.id} is kind: 'wait' but waitIntervalMs/waitTimeoutMs are required on a locked tree`, path });
  }
  if (node.waitTimeoutMs < node.waitIntervalMs) {
    return issue(ctx, { message: `node ${node.id} waitTimeoutMs (${node.waitTimeoutMs}) must be >= waitIntervalMs (${node.waitIntervalMs})`, path });
  }
  const nodeById = new Map(graph.nodes.map(n => [n.id, n]));
  const waitChildren = childrenByParent(graph.edges).get(node.id) ?? [];
  if (waitChildren.length === 0) {
    return issue(ctx, { message: `node ${node.id} is kind: 'wait' but has no outgoing edges — Wait cannot be a leaf`, path });
  }
  for (const childId of waitChildren) {
    const child = nodeById.get(childId);
    if (child && !child.expectedState) {
      return issue(ctx, { message: `node ${childId} has no expectedState but is a child of wait node ${node.id} — every Wait child must have a screenshot`, path });
    }
    if (child?.kind === 'wait') {
      return issue(ctx, { message: `node ${childId} is kind: 'wait' nested under wait node ${node.id} — Wait cannot parent Wait`, path });
    }
  }
  return false;
}

function refineWaitNodes(graph: MacroTreeGraphInput, ctx: z.RefinementCtx): boolean {
  for (const node of graph.nodes) {
    if (node.kind !== 'wait') continue;
    if (refineWaitNode(graph, node, ctx)) return true;
  }
  return false;
}

function refineLaunchGraph(graph: MacroTreeGraphInput, ctx: z.RefinementCtx): void {
  if (refineNodeKinds(graph, ctx)) return;
  if (refineBranchSiblings(graph, ctx)) return;
  if (refineWaitNodes(graph, ctx)) return;
  // Screenshot-node wait (ERROR_STATES S1–S5).
  refineScreenshotWaitLock(graph, ctx);
}

/**
 * A `watcher` tree is the scheduled-watcher reference: exactly one node, which
 * is the root, carries an `expectedState`, runs no actions, and declares the
 * check deadline in `waitTimeoutMs` (1 s .. 30 s).
 */
function refineWatcherGraph(graph: MacroTreeGraphInput, ctx: z.RefinementCtx): void {
  const path = ['nodes'];
  if (graph.nodes.length !== 1) {
    issue(ctx, { message: `watcher tree must have exactly 1 node (got ${graph.nodes.length})`, path });
    return;
  }
  const node = graph.nodes[0];
  if (node.id !== graph.rootNodeId || node.kind !== 'start') {
    issue(ctx, { message: `watcher node ${node.id} must be the root with kind === 'start'`, path: ['rootNodeId'] });
    return;
  }
  if (!node.expectedState) {
    issue(ctx, { message: `watcher node ${node.id} must carry expectedState`, path });
    return;
  }
  const timelineEvents = node.actionTimeline?.events.length ?? 0;
  if (node.macros.length > 0 || timelineEvents > 0) {
    issue(ctx, { message: `watcher node ${node.id} must not run actions (macros or actionTimeline events)`, path });
    return;
  }
  if (node.waitTimeoutMs === undefined
    || node.waitTimeoutMs < MACRO_WATCHER_DEADLINE_MIN_MS || node.waitTimeoutMs > MACRO_WATCHER_DEADLINE_MAX_MS) {
    issue(ctx, {
      message: `watcher node ${node.id} waitTimeoutMs must be within ${MACRO_WATCHER_DEADLINE_MIN_MS}..${MACRO_WATCHER_DEADLINE_MAX_MS} ms`,
      path,
    });
  }
}

/** Entry point used by `macroTreeGraphSchema.superRefine`. */
export function refineLockedMacroTreeGraph(graph: MacroTreeGraphInput, ctx: z.RefinementCtx): void {
  if (refineIdentity(graph, ctx)) return;
  if (refineTopology(graph, ctx)) return;
  if (graph.trigger === 'watcher') {
    refineWatcherGraph(graph, ctx);
    return;
  }
  refineLaunchGraph(graph, ctx);
}

/**
 * Authoring validation for a graph submitted for analysis or lock.
 *
 * Accepts exactly the empty representation (no nodes, no edges, empty root —
 * a deliberate disabled revision) or a non-empty graph with globally unique
 * identities, a resolved root, and acyclic edges. Total reachability is NOT
 * required here: disconnected authoring components are preserved for the
 * author, never analyzed or executed, and Lock rejects them separately with
 * `disconnected_components` once the projection cannot cover every node.
 *
 * Exported for `macroTreeGraphAuthoringSchema`. Runtime consumers MUST NOT
 * execute this shape directly; only `macroExecutionGraphSchema` may cross the
 * analysis/execution boundary.
 */
export function refineAuthoringMacroTreeGraph(graph: MacroTreeGraphInput, ctx: z.RefinementCtx): void {
  if (graph.nodes.length === 0 && graph.edges.length === 0) {
    if (graph.rootNodeId !== '') {
      issue(ctx, { message: "rootNodeId must be '' when nodes and edges are empty", path: ['rootNodeId'] });
    }
    return;
  }
  if (refineIdentity(graph, ctx)) return;
  if (!isAcyclic(graph.nodes.map(node => node.id), graph.edges)) {
    issue(ctx, { message: 'graph must be acyclic', path: ['edges'] });
  }
}
